import {type Action, type Mutation} from '@sanity/client'
import {DocumentId, getDraftId, getPublishedId, getVersionId} from '@sanity/id-utils'
import {jsonMatch} from '@sanity/json-match'
import {type SanityDocument} from 'groq'
import {type ExprNode} from 'groq-js'
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  firstValueFrom,
  groupBy,
  map,
  mergeMap,
  Observable,
  of,
  pairwise,
  retry,
  startWith,
  Subject,
  switchMap,
  tap,
  throttle,
  throwError,
  timer,
  withLatestFrom,
} from 'rxjs'

import {getCurrentUserState} from '../auth/authStore'
import {type ClientOptions, getClientState} from '../client/clientStore'
import {
  type DocumentHandle,
  type DocumentResource,
  isCanvasResource,
  isDatasetResource,
  isMediaLibraryResource,
} from '../config/sanityConfig'
import {isReleasePerspective} from '../releases/utils/isReleasePerspective'
import {
  bindActionByResource,
  type BoundResourceKey,
  type StoreAction,
} from '../store/createActionBinder'
import {type SanityInstance} from '../store/createSanityInstance'
import {createStateSourceAction, type StateSource} from '../store/createStateSourceAction'
import {defineStore, type StoreContext} from '../store/defineStore'
import {type DocumentAction} from './actions'
import {
  API_VERSION,
  INITIAL_OUTGOING_THROTTLE_TIME,
  OUT_OF_SYNC_RETRY_BASE_DELAY,
  OUT_OF_SYNC_RETRY_MAX_DELAY,
} from './documentConstants'
import {
  type DocumentEvent,
  type DocumentTransactionSubmissionResult,
  getDocumentEvents,
} from './events'
import {listen, OutOfSyncError} from './listen'
import {type JsonMatch} from './patchOperations'
import {
  calculatePermissions,
  createGrantsLookup,
  type DatasetAcl,
  type DocumentPermissionsResult,
  type Grant,
} from './permissions'
import {ActionError} from './processActions/processActions'
import {isReleaseAction} from './processActions/releaseUtil'
import {
  type AppliedTransaction,
  applyFirstQueuedTransaction,
  applyRemoteDocument,
  cleanupOutgoingTransaction,
  manageSubscriberIds,
  type OutgoingTransaction,
  type QueuedTransaction,
  removeQueuedTransaction,
  revertOutgoingTransaction,
  transitionAppliedTransactionsToOutgoing,
  type UnverifiedDocumentRevision,
} from './reducers'
import {createFetchDocument, createSharedListener, type SharedListener} from './sharedListener'

export interface DocumentStoreState {
  documentStates: {[TDocumentId in string]?: DocumentState}
  queued: QueuedTransaction[]
  applied: AppliedTransaction[]
  outgoing?: OutgoingTransaction
  grants?: Record<Grant, ExprNode>
  /**
   * The current user's identity (their user ID).
   */
  identity?: string
  error?: unknown
  sharedListener: SharedListener
  fetchDocument: (documentId: string) => Observable<SanityDocument | null>
  events: Subject<DocumentEvent>
}

export interface DocumentState {
  id: string
  /**
   * the "remote" local copy that matches the server. represents the last known
   * server state. this gets updated every time we confirm remote patches
   */
  remote?: SanityDocument | null
  /**
   * the current ephemeral working copy that includes local optimistic changes
   * that have not yet been confirmed by the server
   */
  local?: SanityDocument | null
  /**
   * the revision that our remote document is at
   */
  remoteRev?: string | null
  /**
   * Array of subscription IDs. This document state will be deleted if there are
   * no subscribers.
   */
  subscriptions: string[]
  /**
   * An object keyed by transaction ID of revisions sent out but that have not
   * yet been verified yet. When an applied transaction is transitioned to an
   * outgoing transaction, it also adds unverified revisions for each document
   * that is part of that outgoing transaction. Transactions are submitted to
   * the server with a locally generated transaction ID. This way we can observe
   * when our transaction comes back through the shared listener. Each listener
   * event that comes back contains a `previousRev`. If we see our own
   * transaction with a different `previousRev` than expected, we can rebase our
   * local transactions on top of this new remote.
   */
  unverifiedRevisions?: {[TTransactionId in string]?: UnverifiedDocumentRevision}
}

export const documentStore = defineStore<DocumentStoreState, BoundResourceKey>({
  name: 'Document',
  getInitialState: (instance, {resource}) => ({
    documentStates: {},
    // these can be emptied on refetch
    queued: [],
    applied: [],
    sharedListener: createSharedListener(instance, resource),
    fetchDocument: createFetchDocument(instance, resource),
    events: new Subject(),
  }),
  initialize(context) {
    const {sharedListener} = context.state.get()
    const subscriptions = [
      subscribeToQueuedAndApplyNextTransaction(context),
      subscribeToSubscriptionsAndListenToDocuments(context),
      subscribeToAppliedAndSubmitNextTransaction(context),
      subscribeToClientAndFetchDatasetAcl(context),
      subscribeToCurrentUserAndSetIdentity(context),
    ]

    return () => {
      sharedListener.dispose()
      subscriptions.forEach((subscription) => subscription.unsubscribe())
    }
  },
})

/**
 * @beta
 * Options for specifying a document and optionally a path within it.
 */
export interface DocumentOptions<
  TPath extends string | undefined = undefined,
  TDocumentType extends string = string,
  TDataset extends string = string,
  TProjectId extends string = string,
> extends DocumentHandle<TDocumentType, TDataset, TProjectId> {
  path?: TPath
}

/** @beta */
export function getDocumentState<
  TDocumentType extends string = string,
  TDataset extends string = string,
  TProjectId extends string = string,
>(
  instance: SanityInstance,
  options: DocumentOptions<undefined, TDocumentType, TDataset, TProjectId>,
): StateSource<SanityDocument<TDocumentType, `${TProjectId}.${TDataset}`> | undefined | null>

/** @beta */
export function getDocumentState<
  TPath extends string = string,
  TDocumentType extends string = string,
  TDataset extends string = string,
  TProjectId extends string = string,
>(
  instance: SanityInstance,
  options: DocumentOptions<TPath, TDocumentType, TDataset, TProjectId>,
): StateSource<
  JsonMatch<SanityDocument<TDocumentType, `${TProjectId}.${TDataset}`>, TPath> | undefined
>

/** @beta */
export function getDocumentState<TData>(
  instance: SanityInstance,
  options: DocumentOptions<string | undefined>,
): StateSource<TData | undefined | null>

/** @beta */
export function getDocumentState(
  ...args: Parameters<typeof _getDocumentState>
): StateSource<unknown> {
  return _getDocumentState(...args)
}

const _getDocumentState = bindActionByResource(
  documentStore,
  createStateSourceAction({
    selector: ({state: {error, documentStates}}, options: DocumentOptions<string | undefined>) => {
      const {documentId: docId, path, liveEdit, perspective} = options
      const documentId = DocumentId(docId)
      if (error) throw error
      let document: SanityDocument | null | undefined

      if (liveEdit) {
        document = documentStates[documentId]?.local
      } else {
        let version: SanityDocument | null | undefined
        if (isReleasePerspective(perspective)) {
          const versionId = getVersionId(documentId, perspective.releaseName)
          version = documentStates[versionId]?.local
          // early exit if we don't have the version document and we're in a release perspective
          if (version === undefined) return undefined
        }
        const draft = documentStates[getDraftId(documentId)]?.local
        const published = documentStates[getPublishedId(documentId)]?.local
        // early exit if we don't have all the documents for draft/published logic
        if (draft === undefined || published === undefined) return undefined
        document = version ?? draft ?? published
      }

      if (!path) return document
      const result = jsonMatch(document, path).next()
      if (result.done) return undefined
      const {value} = result.value
      return value
    },
    onSubscribe: (context, options: DocumentOptions<string | undefined>) =>
      manageSubscriberIds(context, [options]),
  }),
)

/** @beta */
export function resolveDocument<
  TDocumentType extends string = string,
  TDataset extends string = string,
  TProjectId extends string = string,
>(
  instance: SanityInstance,
  docHandle: DocumentHandle<TDocumentType, TDataset, TProjectId>,
): Promise<SanityDocument<TDocumentType, `${TProjectId}.${TDataset}`> | null>
/** @beta */
export function resolveDocument<TData extends SanityDocument>(
  instance: SanityInstance,
  docHandle: DocumentHandle<string, string, string>,
): Promise<TData | null>
/** @beta */
export function resolveDocument(
  ...args: Parameters<typeof _resolveDocument>
): Promise<SanityDocument | null> {
  return _resolveDocument(...args)
}
const _resolveDocument = bindActionByResource(
  documentStore,
  ({instance}, docHandle: DocumentHandle<string, string, string>) => {
    return firstValueFrom(
      getDocumentState(instance, {
        ...docHandle,
        path: undefined,
      }).observable.pipe(filter((i) => i !== undefined)),
    ) as Promise<SanityDocument | null>
  },
)

/** @beta */
export const getDocumentSyncStatus = bindActionByResource(
  documentStore,
  createStateSourceAction({
    selector: (
      {state: {error, documentStates: documents, outgoing, applied, queued}},
      doc: DocumentHandle,
    ) => {
      const documentId = DocumentId(typeof doc === 'string' ? doc : doc.documentId)
      if (error) throw error

      if (doc.liveEdit) {
        // For liveEdit documents, only check the single document
        if (documents[documentId] === undefined) return undefined
      } else {
        const version = isReleasePerspective(doc.perspective)
          ? documents[getVersionId(documentId, doc.perspective.releaseName)]
          : undefined
        if (isReleasePerspective(doc.perspective) && version === undefined) return undefined
        // Standard draft/published logic
        const draft = documents[getDraftId(documentId)]
        const published = documents[getPublishedId(documentId)]
        if (draft === undefined || published === undefined) return undefined
      }
      return !queued.length && !applied.length && !outgoing
    },
    onSubscribe: (context, doc: DocumentHandle) => {
      return manageSubscriberIds(context, [doc])
    },
  }),
)

type PermissionsStateOptions = {
  resource?: DocumentResource
  actions: DocumentAction[]
}

/** @beta */
export const getPermissionsState = bindActionByResource(
  documentStore,
  createStateSourceAction({
    selector: calculatePermissions,
    onSubscribe: (context, {actions}: PermissionsStateOptions) => {
      manageSubscriberIds(context, actions)
    },
  }) as StoreAction<
    DocumentStoreState,
    [PermissionsStateOptions],
    StateSource<DocumentPermissionsResult>
  >,
)

/** @beta */
export const resolvePermissions = bindActionByResource(
  documentStore,
  ({instance}, options: PermissionsStateOptions) => {
    return firstValueFrom(
      getPermissionsState(instance, options).observable.pipe(filter((i) => i !== undefined)),
    )
  },
)

/** @beta */
export const subscribeDocumentEvents = bindActionByResource(
  documentStore,
  ({state}, options: {resource?: DocumentResource; eventHandler: (e: DocumentEvent) => void}) => {
    const {events} = state.get()
    const subscription = events.subscribe(options.eventHandler)
    return () => subscription.unsubscribe()
  },
)

const subscribeToQueuedAndApplyNextTransaction = ({
  state,
}: StoreContext<DocumentStoreState, BoundResourceKey>) => {
  const {events} = state.get()
  return state.observable
    .pipe(
      map(applyFirstQueuedTransaction),
      distinctUntilChanged(),
      tap((next) => state.set('applyFirstQueuedTransaction', next)),
      catchError((error, caught) => {
        if (error instanceof ActionError) {
          state.set('removeQueuedTransaction', (prev) =>
            removeQueuedTransaction(prev, error.transactionId),
          )
          events.next({
            type: 'error',
            message: error.message,
            documentId: error.documentId,
            transactionId: error.transactionId,
            error,
          })
          return caught
        }

        throw error
      }),
    )
    .subscribe({error: (error) => state.set('setError', {error})})
}

const subscribeToAppliedAndSubmitNextTransaction = ({
  state,
  instance,
  key: {resource},
}: StoreContext<DocumentStoreState, BoundResourceKey>) => {
  const {events} = state.get()

  return state.observable
    .pipe(
      throttle(
        (s) =>
          // if there is no outgoing transaction, we can throttle by the
          // initial outgoing throttle time…
          !s.outgoing
            ? timer(INITIAL_OUTGOING_THROTTLE_TIME)
            : // …otherwise, wait until the outgoing has been cleared
              state.observable.pipe(first(({outgoing}) => !outgoing)),
        {leading: false, trailing: true},
      ),
      map(transitionAppliedTransactionsToOutgoing),
      distinctUntilChanged((a, b) => a.outgoing?.transactionId === b.outgoing?.transactionId),
      tap((next) => state.set('transitionAppliedTransactionsToOutgoing', next)),
      map((s) => s.outgoing),
      distinctUntilChanged(),
      withLatestFrom(
        getClientState(instance, {
          apiVersion: API_VERSION,
          resource,
        }).observable,
      ),
      concatMap(([outgoing, client]) => {
        if (!outgoing) return EMPTY

        const revertOnError = catchError((error: unknown) => {
          state.set('revertOutgoingTransaction', revertOutgoingTransaction)
          const message = error instanceof Error ? error.message : 'Request failed'
          events.next({type: 'reverted', message, outgoing, error})
          return EMPTY
        })

        const toResult = map((result: unknown) => ({
          result: result as DocumentTransactionSubmissionResult,
          outgoing,
        }))

        // liveEdit transactions route to the mutations API; everything else routes
        // to the actions API. processActions rejects transactions that mix the two,
        // and reducers won't batch across that boundary, so a batch is always
        // entirely liveEdit or entirely not.
        if (outgoing.actions.some((action) => !isReleaseAction(action) && action.liveEdit)) {
          return client.observable
            .mutate(outgoing.outgoingMutations as Mutation[], {
              transactionId: outgoing.transactionId,
              visibility: 'async',
              returnDocuments: false,
              returnFirst: false,
              tag: 'document.mutate',
              skipCrossDatasetReferenceValidation: true,
            })
            .pipe(revertOnError, toResult)
        }

        return client.observable
          .action(outgoing.outgoingActions as Action[], {
            transactionId: outgoing.transactionId,
            skipCrossDatasetReferenceValidation: true,
            tag: 'document.action',
          })
          .pipe(revertOnError, toResult)
      }),
      tap(({outgoing, result}) => {
        state.set('cleanupOutgoingTransaction', cleanupOutgoingTransaction)
        for (const e of getDocumentEvents(outgoing)) events.next(e)
        events.next({type: 'accepted', outgoing, result})
      }),
    )
    .subscribe({error: (error) => state.set('setError', {error})})
}

const subscribeToSubscriptionsAndListenToDocuments = (
  context: StoreContext<DocumentStoreState, BoundResourceKey>,
) => {
  const {state} = context
  const {events} = state.get()

  return state.observable
    .pipe(
      filter((s) => !!s.grants),
      map((s) => Object.keys(s.documentStates)),
      distinctUntilChanged((curr, next) => {
        if (curr.length !== next.length) return false
        const currSet = new Set(curr)
        return next.every((i) => currSet.has(i))
      }),
      startWith(new Set<string>()),
      pairwise(),
      switchMap((pair) => {
        const [curr, next] = pair.map((ids) => new Set(ids))
        const added = Array.from(next).filter((i) => !curr.has(i))
        const removed = Array.from(curr).filter((i) => !next.has(i))

        // NOTE: the order of which these go out is somewhat important
        // because that determines the order `applyRemoteDocument` is called
        // which in turn determines which document version get populated
        // first. because we prefer drafts, it's better to have those go out
        // first so that the published document doesn't flash for a frame
        const changes = [
          ...added.map((id) => ({id, add: true})),
          ...removed.map((id) => ({id, add: false})),
        ].sort((a, b) => {
          const aIsDraft = a.id === getDraftId(DocumentId(a.id))
          const bIsDraft = b.id === getDraftId(DocumentId(b.id))

          if (aIsDraft && bIsDraft) return a.id.localeCompare(b.id, 'en-US')
          if (aIsDraft) return -1
          if (bIsDraft) return 1
          return a.id.localeCompare(b.id, 'en-US')
        })

        return of<{id: string; add: boolean}[]>(...changes)
      }),
      groupBy((i) => i.id),
      mergeMap((group) =>
        group.pipe(
          switchMap((e) => {
            if (!e.add) return EMPTY
            return listen(context, e.id).pipe(
              retry({
                delay: (error, retryCount) => {
                  if (!(error instanceof OutOfSyncError)) return throwError(() => error)
                  const backoff = Math.min(
                    OUT_OF_SYNC_RETRY_BASE_DELAY * 2 ** (retryCount - 1),
                    OUT_OF_SYNC_RETRY_MAX_DELAY,
                  )
                  return timer(backoff)
                },
              }),
              tap((remote) =>
                state.set('applyRemoteDocument', (prev) =>
                  applyRemoteDocument(prev, remote, events),
                ),
              ),
            )
          }),
        ),
      ),
    )
    .subscribe({error: (error) => state.set('setError', {error})})
}

const subscribeToClientAndFetchDatasetAcl = ({
  instance,
  state,
  key: {resource},
}: StoreContext<DocumentStoreState, BoundResourceKey>) => {
  const clientOptions: ClientOptions = {apiVersion: API_VERSION, resource}

  let uri: string
  if (resource && isDatasetResource(resource)) {
    uri = `/projects/${resource.projectId}/datasets/${resource.dataset}/acl`
  } else if (resource && isMediaLibraryResource(resource)) {
    uri = `/media-libraries/${resource.mediaLibraryId}/acl`
  } else if (resource && isCanvasResource(resource)) {
    uri = `/canvases/${resource.canvasId}/acl`
  } else {
    throw new Error(`Received invalid resource: ${JSON.stringify(resource)}`)
  }

  return getClientState(instance, clientOptions)
    .observable.pipe(
      switchMap((client) =>
        client.observable.request<DatasetAcl>({
          uri,
          tag: 'acl.get',
          withCredentials: true,
        }),
      ),
      tap((datasetAcl) => state.set('setGrants', {grants: createGrantsLookup(datasetAcl)})),
    )
    .subscribe({
      error: (error) => state.set('setError', {error}),
    })
}

const subscribeToCurrentUserAndSetIdentity = ({
  instance,
  state,
}: StoreContext<DocumentStoreState, BoundResourceKey>) =>
  getCurrentUserState(instance).observable.subscribe({
    next: (currentUser) => state.set('setIdentity', {identity: currentUser?.id}),
    // A transient identity-fetch failure (network blip, expired token, or a
    // normal logout/re-login transition) should not brick all document
    // operations. Reset the identity to `undefined` and keep going — GROQ's
    // `identity()` then evaluates to null, matching the unauthenticated state.
    error: () => state.set('setIdentity', {identity: undefined}),
  })
